import React, { CSSProperties } from 'react';
import { CategoryConfigurationDefinitionResponse, SkuType } from '@ydistri/api-sdk';
import { styled } from 'styled-components';
import useCategoryId from '../../../../../hooks/useCategoryId';
import { useCategoryConfiguration } from '../../../../../hooks/useCategoryConfiguration';
import { COLORS, computeRemSize } from '@ydistri/theme';
import { Row } from '@ydistri/ds';
import { Popover } from 'antd';

interface ConfigurationRedistributionSetsIndicatorProps {
  definitionData: CategoryConfigurationDefinitionResponse[];
  width?: CSSProperties['width'];
}

const ConfigurationRedistributionSetsIndicatorWrapper = styled(Row)<{
  width?: CSSProperties['width'];
}>`
  padding-left: 1rem;
  ${props => {
    if (props.width) {
      return `width: ${props.width}`;
    }
  }}
`;

/**
 * Circle with filled background when value is '1' (on) or no background when value is zero or undefined
 */
export const SwitchIndicator = styled.div<{ value?: string }>`
  margin-right: ${computeRemSize(5)};
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  ${props => {
    if (props.value === '1') {
      return `background-color: ${COLORS.ON_STATE};`;
    } else {
      return `border: 1px solid ${COLORS.BAR_GREY};`;
    }
  }};
`;

interface RedistributionSetIndicatorProps {
  fieldDefinition: CategoryConfigurationDefinitionResponse;
  value?: string;
}

/**
 * Displays SwitchIndicator with popup showing the fields title and value
 * @param fieldDefinition
 * @param value
 */
const RedistributionSetIndicator: React.FC<RedistributionSetIndicatorProps> = ({
  fieldDefinition,
  value,
}) => {
  const valueText = value === '1' ? 'On' : 'Off';
  const popoverContent = `${fieldDefinition.title}: ${valueText}`;
  return (
    <Popover content={popoverContent}>
      <SwitchIndicator value={value} />
    </Popover>
  );
};

/**
 * Displays a series of SwitchIndicators for each definition
 * @param definitionData one or more data definitions to display for current category
 * @param width required width of the component, controls will be left-aligned
 */
const ConfigurationRedistributionSetsIndicator: React.FC<
  ConfigurationRedistributionSetsIndicatorProps
> = ({ definitionData, width }) => {
  const selectedCategoryId = useCategoryId();
  const { categoryConfigurationData: configurations } =
    useCategoryConfiguration(selectedCategoryId);

  //Redistribution Sets fields are of skuType = null, which is stored as FastMover
  const skuType = SkuType.FastMover;

  return (
    <ConfigurationRedistributionSetsIndicatorWrapper width={width}>
      {definitionData.map(item => {
        return (
          <RedistributionSetIndicator
            key={item.fieldType}
            fieldDefinition={item}
            value={configurations?.[item.fieldType][skuType]?.value}
          />
        );
      })}
    </ConfigurationRedistributionSetsIndicatorWrapper>
  );
};

export default ConfigurationRedistributionSetsIndicator;
